import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from "@mui/material"
import { BsFillArrowUpCircleFill } from "react-icons/bs"
import Carousel, { Modal, ModalGateway } from "react-images";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinearProgressLine from "../LinearProgressLine"
import BaseButton from "../BaseButton";
import BaseImage from "../BaseImage";
import SwipeableViews from "../Swipeable";
import { setOpenChatModal } from "../../store";
import GeneButtonList from "../GeneButtonList";

const MedicationAccordion = ({ item, isTalkToUse = false, zIndex=1, onClick }) => {
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
  const [tagExpanded, setTagExpanded] = useState(false);

  const images = useMemo(() => item?.descriptionImage ? [{
    src: item?.descriptionImage
  }] : [], [item]);

  const handleClickTalkToUse = () => {
    dispatch(setOpenChatModal(true))
  }

  const handleClickOpenLightbox = useCallback((index) => {
    setCurrentImage(index);
    setViewerIsOpen(true);
  }, [])

  const closeLightbox = () => {
    setCurrentImage(0);
    setViewerIsOpen(false);
  };

  return (
    <Accordion
      expanded={expanded}
      onClick={onClick}
      sx={{
        width: '100%',
        // marginBottom: "15px",
        backgroundColor: "primary.white",
        boxShadow: '0px 2px 10px 0px #5B87EF33',
        zIndex: zIndex
      }}
    >
      <AccordionSummary
        aria-controls="medication-content"
        id="medication-header"
        sx={{
          cursor: 'pointer'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography variant="h3">
              {item.title}
            </Typography>
            <ExpandMoreIcon
              sx={{
                rotate: expanded ? '180deg' : '0deg',
                color: 'primary.mainBlue'
              }}
            />
          </Box>
          <Typography variant="subText" color="primary.darkGray">
            {item.category}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              py: '24px'
            }}
          >
            <Typography
              variant="subText"
              color="primary.darkGreen"
              sx={{
                color: item?.guidanceLevel === 'High' ? 'primary.red' : item?.guidanceLevel === 'Moderate' ? 'primary.black' : 'primary.darkGreen'
              }}
            >
              Risk factor: {item?.guidanceLevel}
            </Typography>
            <LinearProgressLine sx={{ mt: 1 }} level={item?.guidanceLevel} value={item.percent} />
          </Box>
          <Stack direction={'row'} flexWrap={'wrap'}>
            {item.brandNames.map(((medicine, idx) => (
              <Box
                key={medicine}
                sx={{
                  display: (idx < 2 || tagExpanded) ? 'block' : 'none',
                  padding: '2px 6px 2px 6px',
                  borderRadius: '4px',
                  bgcolor: 'primary.lightGray',
                  mr: '4px',
                  mt: '2px'
                }}
              >
                <Typography variant="body" color="primary.darkGray">
                  {medicine}
                </Typography>
              </Box>
            )))}
            <Box
              sx={{
                display: (item.brandNames && item.brandNames.length > 2 && !tagExpanded) ? 'block' : 'none',
                padding: '2px 6px 2px 6px',
                borderRadius: '4px',
                bgcolor: 'primary.lightGray',
                mr: '4px',
                mt: '2px'
              }}
              onClick={() => setTagExpanded(true)}
            >
              <Typography variant="body" color="primary.darkGray">
                {item.brandNames.length - 2}+
              </Typography>
            </Box>
          </Stack>
          {(isTalkToUse && item?.guidanceLevel === 'High') && (
            <BaseButton
              buttonType={'default'}
              sx={{
                maxWidth: { md: '138px' },
                height: '32px',
                mt: '16px'
              }}
              onClick={handleClickTalkToUse}
            >
              <Typography variant="body">
                Talk to us
              </Typography>
            </BaseButton>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography variant="subText" color="primary.darkGray">
            Description
          </Typography>
          <Typography variant="h4" mt={'4px'}>
            {item.descriptionTitle}
          </Typography>
          <Typography variant="body" color="primary.darkGray" mt={2} whiteSpace={'pre-wrap'}>
            <div dangerouslySetInnerHTML={{__html: item.description}}></div>
          </Typography>
          {images && (
            <SwipeableViews>
              {images.map(((image, index) => (
                <BaseImage
                  key={index}
                  src={image.src}
                  sx={{
                    mt: '16px',
                    cursor: 'pointer'
                  }}
                  sximage={{
                    borderRadius: '8px',
                    height: '80px',
                    width: '180px',
                    objectFit: 'cover'
                  }}
                  onClick={() => handleClickOpenLightbox(index)}
                />
              )))}
            </SwipeableViews>
          )}
          <ModalGateway>
            {viewerIsOpen ? (
              <Modal onClose={closeLightbox}>
                <Carousel
                  currentIndex={currentImage}
                  views={images.map(x => ({
                    ...x,
                    srcset: x.srcSet,
                    caption: x.title
                  }))}
                />
              </Modal>
            ) : null}
          </ModalGateway>
          <Stack spacing={'8px'} mt="24px">
            <Box 
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'primary.mediumGray',
                padding: '8px',
              }}
            >
              <Typography variant="subText" color="primary.darkGray">Gene:</Typography>
              {/* <Typography variant="h4" mt="2px">{item?.genes?.toString()}</Typography> */}
              <Box mt="2px">
                <GeneButtonList genes={item?.genes}/>
              </Box>
            </Box>
            <Box 
              sx={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'primary.mediumGray',
                padding: '8px',
                cursor: 'pointer',

                '&:hover': {
                  borderColor: 'primary.darkGreen',
                  bgcolor: 'primary.lightGreen'
                }
              }}
            >
              <Typography variant="subText" color="primary.darkGray">Class:</Typography>
              <Typography variant="h4" mt="2px">{item?.class}</Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              mt: '24px'
            }}
          >
            <Box sx={{
              color: 'primary.red',
              mr: '4px'
            }}>
              <BsFillArrowUpCircleFill size={20} />
            </Box>
            <Typography variant="h4">
              Evidence: {item.evidenceLevel}
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default MedicationAccordion